import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";

import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineWarnButton } from "components/buttons/OutlineWarnButton";

import { ReactComponent as CopyIcon } from "assets/images/icons/btn_copy.svg";
import { ReactComponent as ClockIcon } from "assets/images/icons/icn_display_clock.svg";
import { ReactComponent as BlockIcon } from "assets/images/icons/icn_table_cell_Block_Red.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_warning_solid.svg";

import SupportToken from "./components/SupportToken";
import Extend from "./components/Extend";
import Revoke from "./components/Revoke";

const RemoteSupport = () => {
  const theme = useTheme();

  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [openExtendModal, setOpenExtendModal] = useState(false);
  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [blockingSession, setBlockingSession] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  const { auth, isAuthReady, standalone } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [stateUuid] = useState(() => uuidv4());
  const [createUuid] = useState(() => uuidv4());
  const [historyUuid] = useState(() => uuidv4());

  const [revokeUuid] = useState(() => uuidv4());
  const createRequestRef = useRef(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [extendUuid] = useState(() => uuidv4());
  const [extendLoading, setExtendLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  const buildApiConfig = (resource, scopeType = "site") => {
    const isSiteScope = scopeType === "site";

    if (standalone) {
      return {
        path: isSiteScope
          ? `/orgs/:orgId/sites/:siteId/${resource}`
          : `/orgs/:orgId/${resource}`,
        params: {
          orgId: auth.realm?.id,
          ...(isSiteScope && {
            siteId: auth.sites?.id,
          }),
        },
      };
    }

    return {
      path: isSiteScope
        ? `/ents/:entId/orgs/:orgId/sites/:siteId/${resource}`
        : `/ents/:entId/orgs/:orgId/${resource}`,
      params: {
        entId: auth.ents?.id,
        orgId: auth.orgs?.id,
        ...(isSiteScope && {
          siteId: auth.sites?.id,
        }),
      },
    };
  };

  const getState = () => {
    const scopeType = getScopeType();

    const api = buildApiConfig("supportTokens/state.read", scopeType);

    sendRequest(api.path, stateUuid, api.params);
  };

  useEffect(() => {
    if (!isAuthReady) return;

    const scopeType = getScopeType();

    if (scopeType === "site" && !auth?.sites?.id) {
      return;
    }

    getState();
    getHistory();
  }, [
    isAuthReady,
    standalone,
    auth?.realm?.id,
    auth?.ents?.id,
    auth?.orgs?.id,
    auth?.sites?.id,
  ]);

  const getHistory = () => {
    const scopeType = getScopeType();

    const api = buildApiConfig("supportTokens/history.read", scopeType);

    sendRequest(api.path, historyUuid, api.params);
  };

  useEffect(() => {
    const response = getData(stateUuid);

    if (!response) return;

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      null;

    if (!responseData) return;

    const { currentToken, blockingToken } = responseData;

    setBlockingSession(blockingToken ?? null);

    setShowToken(false);
    setCopied(false);

    const tokenData = currentToken || blockingToken;

    setActiveSession(tokenData);
    setShowToken(false);
    setCopied(false);
  }, [dataMap, getData, stateUuid]);

  useEffect(() => {
    const response = getData(historyUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Get support token history failed:", response.error);
      setHistoryData([]);
      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      response?.result ??
      null;

    const historyList = Array.isArray(responseData)
      ? responseData
      : (responseData?.history ??
        responseData?.items ??
        responseData?.list ??
        responseData?.supportTokens ??
        []);

    const selectedScope = getSelectedScopeName();

    const normalizedHistory = historyList.map((item, index) => {
      const scopeType = item?.scopeType;

      const generatedTimestamp =
        item?.generatedOn ?? item?.generatedAt ?? item?.createdAt ?? null;

      const endedTimestamp =
        item?.endedOn ??
        item?.endedAt ??
        item?.resolvedAt ??
        item?.revokedAt ??
        item?.expiredAt ??
        null;

      return {
        id: `${item?.supportTokenId ?? item?.id ?? "support-history"}-${index}`,

        generatedOn: formatApiDateTime(generatedTimestamp),

        generatedBy: item?.generatedBy ?? item?.createdBy ?? "-",

        scope:
          item?.scope ??
          item?.scopeLabel ??
          (scopeType === "org"
            ? `Organization: ${item?.orgName || selectedScope.name}`
            : `Site: ${item?.siteName || selectedScope.name}`),

        accessLevel:
          item?.accessLevel === "full_control"
            ? "Full-control"
            : item?.accessLevel === "read_only"
              ? "Read-only"
              : item?.accessLevel || "-",

        currentSupportDisplayName: item?.currentSupportDisplayName ?? "-",

        status: formatHistoryStatus(item?.status),

        endedOn: formatApiDateTime(endedTimestamp),
      };
    });

    setHistoryData(normalizedHistory);
  }, [dataMap, getData, historyUuid]);

  const getSelectedScopeName = () => {
    try {
      const selectBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );

      const realm = selectBreadcrumbs?.realm || {};
      const org = selectBreadcrumbs?.orgs || {};
      const site = selectBreadcrumbs?.site || {};

      // Site scope
      if (site?.id || Object.keys(site).length > 0) {
        return {
          type: "Site",
          name: site?.name || "-",
        };
      }

      // Managed Organization
      if (org?.id || Object.keys(org).length > 0) {
        return {
          type: "Organization",
          name: org?.name || "-",
        };
      }

      // Standalone Organization：
      // orgs 是空物件，Organization 資料放在 realm
      if (realm?.id && realm?.hasManageOrg === false) {
        return {
          type: "Organization",
          name: realm?.name || "-",
        };
      }

      return {
        type: "Organization",
        name: realm?.name || "-",
      };
    } catch (error) {
      console.error("Failed to parse selectBreadcrumbs:", error);

      return {
        type: "-",
        name: "-",
      };
    }
  };

  const getScopeType = () => {
    try {
      const selectBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs"),
      );

      if (selectBreadcrumbs?.site?.id) {
        return "site";
      }

      return "org";
    } catch (error) {
      console.error("Failed to parse selectBreadcrumbs:", error);

      return "org";
    }
  };

  const handleGenerateSupportToken = () => {
    setOpenGenerateModal(true);
  };

  const handleCloseGenerateDialog = () => {
    setOpenGenerateModal(false);
  };

  const handleNext = (formData) => {
    return new Promise((resolve, reject) => {
      const scopeType = formData?.scopeType || getScopeType();

      const api = buildApiConfig("supportTokens.create", scopeType);

      const requestData = {
        scopeType,
        accessLevel: formData.accessLevel,
        expireAt: Math.floor(formData.expireAt / 1000),
        reason: formData.reason || "",
      };

      createRequestRef.current = {
        resolve,
        reject,
      };

      try {
        sendRequest(api.path, createUuid, api.params, {
          updates: requestData,
        });
      } catch (error) {
        createRequestRef.current = null;
        reject(error);
      }
    });
  };
  useEffect(() => {
    const response = getData(createUuid);

    if (!response || !createRequestRef.current) {
      return;
    }

    const pendingRequest = createRequestRef.current;

    if (response?.error) {
      const message =
        response.error?.message || "Failed to generate support token.";

      pendingRequest.reject(new Error(message));
      createRequestRef.current = null;
      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      null;

    if (!responseData?.token) {
      console.error("Create support token response has no token:", response);

      pendingRequest.reject(
        new Error("The API response did not contain a support token."),
      );

      createRequestRef.current = null;
      return;
    }

    pendingRequest.resolve(responseData);
    createRequestRef.current = null;
  }, [dataMap, getData, createUuid]);

  useEffect(() => {
    const response = getData(revokeUuid);

    if (!response || !revokeLoading) {
      return;
    }

    if (response?.error) {
      console.error("Revoke support token failed:", response.error);

      setRevokeLoading(false);
      return;
    }

    setActiveSession(null);
    setShowToken(false);
    setCopied(false);
    setOpenRevokeModal(false);
    setRevokeLoading(false);

    getState();
    getHistory();
  }, [dataMap, getData, revokeUuid, revokeLoading]);

  const handleSupportTokenDone = (sessionData) => {
    const selectedScope = getSelectedScopeName();

    const normalizedSession = {
      supportTokenId: sessionData?.supportTokenId ?? sessionData?.id ?? null,

      token: sessionData?.token || "",

      scopeType: sessionData?.scopeType || getScopeType(),

      scope:
        sessionData?.scope ||
        sessionData?.scopeLabel ||
        `${selectedScope.type}: ${selectedScope.name}`,

      scopeLabel:
        sessionData?.scopeLabel ||
        `${selectedScope.type}: ${selectedScope.name}`,

      accessLevel:
        sessionData?.accessLevel === "full_control"
          ? "Full-control"
          : sessionData?.accessLevel === "read_only"
            ? "Read-only"
            : sessionData?.accessLevel || "Read-only",

      generatedBy: sessionData?.generatedBy,

      generatedOn:
        sessionData?.generatedOn ?? sessionData?.generatedAt ?? Date.now(),

      expireAt: sessionData?.expireAt ?? null,

      currentSupportDisplayName: sessionData?.currentSupportDisplayName ?? "-",

      reason: sessionData?.reason || "",

      status: "active",
    };

    setActiveSession(normalizedSession);
    setShowToken(false);
    setCopied(false);
    setOpenGenerateModal(false);

    getHistory();
  };

  const handleCopyToken = async () => {
    if (!activeSession?.token) {
      return;
    }

    try {
      await navigator.clipboard.writeText(activeSession.token);

      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Failed to copy support token:", error);
    }
  };

  const handleExtendToken = () => {
    if (!activeSession) {
      return;
    }

    setOpenExtendModal(true);
  };

  const handleCloseExtendModal = () => {
    setOpenExtendModal(false);
  };

  const handleConfirmExtendToken = (newExpireAt) => {
    if (extendLoading) {
      return;
    }

    const supportTokenId = activeSession?.supportTokenId ?? activeSession?.id;

    if (!supportTokenId) {
      console.error("supportTokenId is missing.", activeSession);
      return;
    }

    const scopeType = activeSession?.scopeType || getScopeType();

    const api = buildApiConfig(
      "supportTokens/:supportTokenId/extension.update",
      scopeType,
    );

    setExtendLoading(true);

    sendRequest(
      api.path,
      extendUuid,
      {
        ...api.params,
        supportTokenId,
      },
      {
        updates: {
          expireAt: Math.floor(newExpireAt / 1000),
        },
      },
    );
  };

  useEffect(() => {
    const response = getData(extendUuid);

    if (!response || !extendLoading) {
      return;
    }

    if (response?.error) {
      console.error("Extend support token failed:", response.error);

      setExtendLoading(false);
      return;
    }

    setActiveSession((previous) => {
      if (!previous) {
        return previous;
      }

      return {
        ...previous,
        expireAt: response?.result?.data?.expireAt
          ? response.result.data.expireAt * 1000
          : previous.expireAt,
      };
    });

    setOpenExtendModal(false);
    setExtendLoading(false);

    getState();
    getHistory();
  }, [dataMap, getData, extendUuid, extendLoading]);
  const handleRevokeToken = () => {
    setOpenRevokeModal(true);
  };

  const handleCloseRevokeModal = () => {
    setOpenRevokeModal(false);
  };

  const handleConfirmRevoke = () => {
    if (revokeLoading) {
      return;
    }

    const supportTokenId = activeSession?.supportTokenId ?? activeSession?.id;

    if (!supportTokenId) {
      console.error(
        "Cannot revoke support token: supportTokenId is missing.",
        activeSession,
      );

      return;
    }

    const scopeType = activeSession?.scopeType || getScopeType();

    const api = buildApiConfig(
      `supportTokens/:supportTokenId/revocation.update`,
      scopeType,
    );

    setRevokeLoading(true);

    sendRequest(api.path, revokeUuid, {
      ...api.params,
      supportTokenId,
    });
  };
  const handleSimulateResolved = () => {
    if (!activeSession) {
      return;
    }

    setActiveSession(null);
    setShowToken(false);
    setCopied(false);
  };

  const normalizeTimestamp = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value < 100000000000 ? value * 1000 : value;
    }

    const str = String(value).trim();

    if (!str) {
      return null;
    }

    if (/^\d+$/.test(str)) {
      const num = Number(str);

      return num < 100000000000 ? num * 1000 : num;
    }

    const parsed = Date.parse(str);

    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatApiDateTime = (timestamp) => {
    const normalizedTimestamp = normalizeTimestamp(timestamp);

    if (!normalizedTimestamp) {
      return "-";
    }

    return new Date(normalizedTimestamp).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatHistoryStatus = (status) => {
    switch (status) {
      case "resolved":
        return "Resolved";

      case "expired":
        return "Expired";

      case "revoked":
        return "Revoked";

      case "active":
        return "Active";

      case "awaiting_access":
        return "Awaiting access";

      default:
        return status || "-";
    }
  };

  const formatDate = (timestamp) => {
    const value = normalizeTimestamp(timestamp);

    if (!value) {
      return "-";
    }

    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Taipei",
    });
  };
  const getRemainingDays = (expireAt) => {
    const value = normalizeTimestamp(expireAt);

    if (!value) {
      return 0;
    }

    const millisecondsPerDay = 24 * 60 * 60 * 1000;

    return Math.max(0, Math.ceil((value - Date.now()) / millisecondsPerDay));
  };

  const getMaskedToken = (token) => {
    if (!token) {
      return "";
    }

    return "•".repeat(Math.max(token.length, 36));
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Active":
        return {
          color: "#07952B",
          backgroundColor: "#E3F5E8",
        };

      case "Resolved":
        return {
          color: "#3D4D9E",
          backgroundColor: "#E9EBFF",
        };

      case "Revoked":
        return {
          color: "#E5004F",
          backgroundColor: "#FFE6EF",
        };

      case "Expired":
        return {
          color: "#8B94A7",
          backgroundColor: "#F0F2F7",
        };

      case "Awaiting access":
        return {
          color: "#A56A00",
          backgroundColor: "#FFF4D6",
        };

      default:
        return {
          color: theme.palette.primary.greyPrimary,
          backgroundColor: "#F0F2F7",
        };
    }
  };

  const tableHeaders = [
    {
      key: "generatedOn",
      label: "Generated on",
      width: "15%",
    },
    {
      key: "generatedBy",
      label: "Generated by",
      width: "24%",
    },
    {
      key: "scope",
      label: "Scope",
      width: "11%",
    },
    {
      key: "accessLevel",
      label: "Access level",
      width: "10%",
    },
    {
      key: "currentSupportDisplayName",
      label: "Support Employee ID",
      width: "14%",
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
    },
    {
      key: "endedOn",
      label: "Ended on",
      width: "16%",
    },
  ];

  const currentStatus = formatHistoryStatus(activeSession?.status);
  const scope = getSelectedScopeName();
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        p: "20px 22px",
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            flexShrink: 0,
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize?.[5],
            fontWeight: theme.typography.customFontWeight?.[1],
          }}
        >
          Remote Support
        </Typography>
      </Box>

      {/* Description */}
      <Typography
        component="div"
        sx={{
          mt: "8px",
          width: "100%",
          minWidth: 0,
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize?.[2],
          fontWeight: theme.typography.customFontWeight?.[0],
          lineHeight: 1.5,
          boxSizing: "border-box",
        }}
      >
        Grant a D-Link support engineer time-boxed, scoped access to{" "}
        <Box
          component="span"
          sx={{
            fontWeight: 700,
          }}
        >
          {scope.type}: {scope.name}
        </Box>
        . Only one support session can be active per organization at a time.
      </Typography>

      {/* Support content */}
      {blockingSession ? (
        <Box
          sx={{
            mt: "16px",
            width: "100%",
            px: "20px",
            py: "18px",
            boxSizing: "border-box",
            border: "1px solid #F0A72E",
            borderRadius: "8px",
            backgroundColor: "#FFF9F0",
          }}
        >
          {/* Warning title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              mb: "12px",
            }}
          >
            <AlertIcon />

            <Typography
              sx={{
                color: "#A96000",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              A support session is already active for this organization.
            </Typography>
          </Box>

          {/* Blocking session information */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "140px 1fr",
                sm: "170px 1fr",
              },
              columnGap: "8px",
              rowGap: "10px",
            }}
          >
            {[
              ["Active at", blockingSession?.scopeLabel || "-"],
              ["Generated by", blockingSession?.generatedBy || "-"],
              [
                "Generated on",
                formatApiDateTime(
                  blockingSession?.generatedOn ??
                    blockingSession?.generatedAt ??
                    blockingSession?.createdAt,
                ),
              ],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <Typography
                  sx={{
                    color: "#778199",
                    fontSize: "14px",
                  }}
                >
                  {label}
                </Typography>

                <Typography
                  sx={{
                    color: "#20377C",
                    fontSize: "14px",
                    fontWeight: 700,
                    wordBreak: "break-word",
                  }}
                >
                  {value || "-"}
                </Typography>
              </React.Fragment>
            ))}

            {/* Status */}
            <Typography
              sx={{
                color: "#778199",
                fontSize: "14px",
              }}
            >
              Status
            </Typography>

            <Box>
              <Chip
                label={formatHistoryStatus(blockingSession?.status)}
                size="small"
                sx={{
                  height: "22px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: 700,

                  ...getStatusStyle(
                    formatHistoryStatus(blockingSession?.status),
                  ),

                  "& .MuiChip-label": {
                    px: "10px",
                  },
                }}
              />
            </Box>

            {/* Support Employee ID */}
            <Typography
              sx={{
                color: "#778199",
                fontSize: "14px",
              }}
            >
              Support Employee ID
            </Typography>

            <Typography
              sx={{
                color: blockingSession?.currentSupportDisplayName
                  ? "#20377C"
                  : "#778199",
                fontSize: "14px",
                fontWeight: 700,
                wordBreak: "break-word",
              }}
            >
              {blockingSession?.currentSupportDisplayName || "Not yet accessed"}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            sx={{
              mt: "12px",
              color: "#778199",
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            Only one support session can be active per organization at a time.
            End the active session on that site&apos;s Support page to start a
            new one here.
          </Typography>
        </Box>
      ) : activeSession ? (
        <Box
          sx={{
            mt: "16px",
            width: "100%",
            border: "1px solid #DDE2EB",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          {/* Active session header */}
          <Box
            sx={{
              minHeight: "50px",
              px: "20px",
              py: "12px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderBottom: "1px solid #DDE2EB",
              backgroundColor: "#FAFBFD",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: "16px",
                fontWeight: 700,
              }}
            >
              Support token
            </Typography>

            <Chip
              label={currentStatus}
              size="small"
              sx={{
                height: "22px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 700,

                ...getStatusStyle(currentStatus),

                "& .MuiChip-label": {
                  px: "10px",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              p: "16px 20px",
            }}
          >
            {/* Token */}
            <Box
              sx={{
                minHeight: "54px",
                px: "14px",
                py: "10px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                border: "1px solid #DCE2FF",
                borderRadius: "7px",
                backgroundColor: "#F2F4FF",
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: "#20377C",
                  fontSize: "14px",
                  fontFamily: "monospace",
                  letterSpacing: showToken ? "0.4px" : "1.5px",
                  overflowWrap: "anywhere",
                }}
              >
                {showToken
                  ? activeSession.token
                  : getMaskedToken(activeSession.token)}
              </Typography>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  gap: "8px",
                }}
              >
                <OutlineRectangleButton
                  width="100px"
                  height="32px"
                  startIcon={
                    showToken ? (
                      <HideIcon
                        sx={{
                          fontSize: "16px !important",
                        }}
                      />
                    ) : (
                      <ShowIcon
                        sx={{
                          fontSize: "16px !important",
                        }}
                      />
                    )
                  }
                  onClick={() => {
                    setShowToken((previous) => !previous);
                  }}
                  sx={{
                    "& .MuiButton-startIcon svg": {
                      width: "20px",
                      height: "20px",
                    },
                  }}
                >
                  {showToken ? "Hide" : "Reveal"}
                </OutlineRectangleButton>

                <OutlineRectangleButton
                  width="100px"
                  height="32px"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyToken}
                  sx={{
                    "& .MuiButton-startIcon svg": {
                      width: "20px",
                      height: "20px",
                    },
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </OutlineRectangleButton>
              </Box>
            </Box>

            {/* Session information */}
            <Box
              sx={{
                mt: "16px",
                display: "grid",
                gridTemplateColumns: {
                  xs: "130px 1fr",
                  sm: "170px 1fr",
                },
                columnGap: "8px",
                rowGap: "10px",
              }}
            >
              {[
                ["Scope", activeSession.scopeLabel],
                ["Access level", activeSession.accessLevel],
                ["Generated by", activeSession.generatedBy],
                ["Generated on", formatApiDateTime(activeSession.createdAt)],
                [
                  "Expires on",
                  activeSession.expireAt
                    ? `${formatDate(
                        activeSession.expireAt,
                      )} (in ${getRemainingDays(activeSession.expireAt)} days)`
                    : "-",
                ],
                [
                  "Support Employee ID",
                  activeSession.currentSupportDisplayName,
                ],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Typography
                    sx={{
                      color: "#778199",
                      fontSize: "14px",
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#20377C",
                      fontSize: "14px",
                      fontWeight: 700,
                      wordBreak: "break-word",
                    }}
                  >
                    {value || "-"}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                mt: "18px",
                pt: "16px",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
                borderTop: "1px solid #EAEDF3",
              }}
            >
              <OutlineButton
                width="100px"
                height="32px"
                startIcon={<ClockIcon />}
                onClick={handleExtendToken}
              >
                Extend
              </OutlineButton>

              <OutlineWarnButton
                width="100px"
                height="32px"
                startIcon={<BlockIcon />}
                onClick={handleRevokeToken}
              >
                Revoke
              </OutlineWarnButton>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            mt: "16px",
            width: "100%",
            minWidth: 0,
            minHeight: "420px",
            boxSizing: "border-box",
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            backgroundImage: `
              repeating-linear-gradient(
                to right,
                #BFC7D5 0,
                #BFC7D5 6px,
                transparent 6px,
                transparent 10px
              ),
              repeating-linear-gradient(
                to right,
                #BFC7D5 0,
                #BFC7D5 6px,
                transparent 6px,
                transparent 10px
              ),
              repeating-linear-gradient(
                to bottom,
                #BFC7D5 0,
                #BFC7D5 6px,
                transparent 6px,
                transparent 10px
              ),
              repeating-linear-gradient(
                to bottom,
                #BFC7D5 0,
                #BFC7D5 6px,
                transparent 6px,
                transparent 10px
              )
            `,

            backgroundSize: `
              calc(100% - 16px) 2px,
              calc(100% - 16px) 2px,
              2px calc(100% - 16px),
              2px calc(100% - 16px)
            `,

            backgroundPosition: `
              8px 0,
              8px 100%,
              0 8px,
              100% 8px
            `,

            backgroundRepeat: "no-repeat",
          }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: "416px",
              px: 3,
              py: 4,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize?.[5],
                fontWeight: theme.typography.customFontWeight?.[1],
                lineHeight: 1.4,
              }}
            >
              No active support session
            </Typography>

            <Typography
              sx={{
                mt: "4px",
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize?.[2],
                fontWeight: theme.typography.customFontWeight?.[0],
                lineHeight: 1.5,
              }}
            >
              Generate a token and send it to your D-Link support contact so
              they can access this scope.
            </Typography>

            <SolidButton
              width="200px"
              height="32px"
              onClick={handleGenerateSupportToken}
              sx={{
                mt: "16px",
              }}
            >
              Generate Support Token
            </SolidButton>
          </Box>
        </Box>
      )}

      {/* History */}
      <Box
        sx={{
          mt: "24px",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            mb: "12px",
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize?.[5],
            fontWeight: theme.typography.customFontWeight?.[1],
          }}
        >
          History
        </Typography>

        <TableContainer
          component={Box}
          sx={{
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            border: "1px solid #E0E5EF",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(31, 42, 68, 0.08)",
          }}
        >
          <Table
            sx={{
              minWidth: "1200px",
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#FAFBFD",
                }}
              >
                {tableHeaders.map((header) => (
                  <TableCell
                    key={header.key}
                    sx={{
                      width: header.width,
                      height: "40px",
                      px: "16px",
                      py: "8px",
                      boxSizing: "border-box",
                      borderBottom: "1px solid #DDE2EB",
                      color: "#778199",
                      fontSize: "12px",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {historyData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-of-type td": {
                      borderBottom: "none",
                    },

                    "&:hover": {
                      backgroundColor: "#FAFBFF",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.generatedOn}
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row.generatedBy}
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.scope}
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.accessLevel}
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.currentSupportDisplayName}
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                    }}
                  >
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        height: "22px",
                        minWidth: "68px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        ...getStatusStyle(row.status),

                        "& .MuiChip-label": {
                          px: "10px",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      px: "16px",
                      py: "12px",
                      borderBottom: "1px solid #EAEDF3",
                      color: theme.palette.primary.greyPrimary,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.endedOn}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box
            sx={{
              width: "100%",
              px: "16px",
              py: "8px",
              boxSizing: "border-box",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography
              sx={{
                color: "#778199",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {historyData.length} sessions
            </Typography>
          </Box>
        </TableContainer>
      </Box>

      <SupportToken
        open={openGenerateModal}
        onClose={handleCloseGenerateDialog}
        onNext={handleNext}
        onDone={handleSupportTokenDone}
        siteName={scope.name}
        scopeName={scope.name}
        scopeLabel={`${scope.type}: ${scope.name}`}
        scopeType={getScopeType()}
      />
      <Extend
        open={openExtendModal}
        loading={extendLoading}
        onClose={handleCloseExtendModal}
        currentExpireAt={activeSession?.expireAt}
        onExtend={handleConfirmExtendToken}
      />

      <Revoke
        open={openRevokeModal}
        loading={revokeLoading}
        onClose={handleCloseRevokeModal}
        onConfirm={handleConfirmRevoke}
      />
    </Box>
  );
};

export default RemoteSupport;
